'use client';

import { c as _c } from "react/compiler-runtime";
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Carousel, type CarouselApi, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { cn } from '@/lib/utils';
import { VideoCard } from './video-card';
export function VideCarousel(t0) {
  const $ = _c(66);
  const {
    videoData
  } = t0;
  const containerRef = useRef(null);
  const autoScrollTimerRef = useRef(null);
  const [api, setApi] = useState();
  const [current, setCurrent] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [autoMuted, setAutoMuted] = useState(true);
  const [manualMuteOverride, setManualMuteOverride] = useState(null);
  const isMuted = manualMuteOverride ?? autoMuted;
  const [isMobile, setIsMobile] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  let t1;
  if ($[0] !== isMuted) {
    t1 = () => {
      if (isMuted) {
        setIsAutoPlay(false);
      }
      setAutoMuted(false);
      setManualMuteOverride(!isMuted);
    };
    $[0] = isMuted;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const toggleMute = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setIsAutoPlay(false);
      setAutoMuted(false);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleUserInteraction = t2;
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const matchMedia = window.matchMedia("(max-width: 600px)");
      if (matchMedia.matches) {
        setIsMobile(true);
      }
    };
    t4 = [];
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  useEffect(_temp2, t5);
  let t6;
  let t7;
  if ($[6] !== api || $[7] !== isAutoPlay) {
    t6 = () => {
      if (!api) {
        return;
      }
      setCurrent(api.selectedScrollSnap());
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
      const updateFocused = () => {
        const engine = api.internalEngine();
        const currentProgress = api.scrollProgress();
        const snapList = engine.scrollSnapList;
        let minDiff = Infinity;
        let nearestSnapIndex = -1;
        snapList.forEach((snap, snapIndex) => {
          let diff = Math.abs(snap - currentProgress);
          if (engine.options.loop) {
            const diffMinus = Math.abs(snap - currentProgress - 1);
            const diffPlus = Math.abs(snap - currentProgress + 1);
            diff = Math.min(diff, diffMinus, diffPlus);
          }
          if (diff < minDiff) {
            minDiff = diff;
            nearestSnapIndex = snapIndex;
          }
        });
        setFocusedIndex(nearestSnapIndex);
      };
      const onSelect = () => {
        setCurrent(api.selectedScrollSnap());
        setCanScrollPrev(api.canScrollPrev());
        setCanScrollNext(api.canScrollNext());
        if (!isAutoPlay) {
          setIsPaused(false);
        }
      };
      const handlePointerDown = () => {
        handleUserInteraction();
        setIsDragging(true);
      };
      const handlePointerUp = () => {
        setIsDragging(false);
      };
      updateFocused();
      api.on("scroll", updateFocused);
      api.on("select", onSelect);
      api.on("reInit", onSelect);
      api.on("pointerDown", handlePointerDown);
      api.on("pointerUp", handlePointerUp);
      api.on("settle", handlePointerUp);
      return () => {
        api.off("scroll", updateFocused);
        api.off("select", onSelect);
        api.off("reInit", onSelect);
        api.off("pointerDown", handlePointerDown);
        api.off("pointerUp", handlePointerUp);
        api.off("settle", handlePointerUp);
      };
    };
    t7 = [api, isAutoPlay, handleUserInteraction];
    $[6] = api;
    $[7] = isAutoPlay;
    $[8] = t6;
    $[9] = t7;
  } else {
    t6 = $[8];
    t7 = $[9];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[10] !== api) {
    t8 = () => {
      const observer = new IntersectionObserver(t10 => {
        const [entry] = t10;
        if (!entry.isIntersecting) {
          setIsAutoPlay(true);
          setAutoMuted(true);
          setManualMuteOverride(null);
          setIsPaused(false);
        } else {
          api?.scrollTo(0, true);
        }
      }, {
        threshold: 0.1
      });
      if (containerRef.current) {
        observer.observe(containerRef.current);
      }
      return () => observer.disconnect();
    };
    t9 = [api];
    $[10] = api;
    $[11] = t8;
    $[12] = t9;
  } else {
    t8 = $[11];
    t9 = $[12];
  }
  useEffect(t8, t9);
  let t10;
  if ($[13] !== api || $[14] !== isAutoPlay) {
    t10 = () => {
      if (autoScrollTimerRef.current) {
        clearTimeout(autoScrollTimerRef.current);
      }
      if (isAutoPlay && api) {
        autoScrollTimerRef.current = setTimeout(() => {
          api.scrollNext();
        }, 3500);
      }
      return () => {
        if (autoScrollTimerRef.current) {
          clearTimeout(autoScrollTimerRef.current);
        }
      };
    };
    $[13] = api;
    $[14] = isAutoPlay;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== api || $[17] !== current || $[18] !== isAutoPlay) {
    t11 = [isAutoPlay, current, api];
    $[16] = api;
    $[17] = current;
    $[18] = isAutoPlay;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  useEffect(t10, t11);
  let t12;
  if ($[20] !== api || $[21] !== isAutoPlay) {
    t12 = () => {
      if (!isAutoPlay) {
        api?.scrollNext();
      }
    };
    $[20] = api;
    $[21] = isAutoPlay;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const handleVideoEnd = t12;
  let t13;
  if ($[23] !== api) {
    t13 = index => {
      if (api) {
        api.scrollTo(index);
        handleUserInteraction();
        setIsPaused(false);
      }
    };
    $[23] = api;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const handleCardClick = t13;
  let t14;
  if ($[25] !== api) {
    t14 = () => {
      handleUserInteraction();
      api?.scrollPrev();
    };
    $[25] = api;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const handlePrev = t14;
  let t15;
  if ($[27] !== api) {
    t15 = () => {
      handleUserInteraction();
      api?.scrollNext();
    };
    $[27] = api;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const handleNext = t15;
  let t16;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = cn(["relative mx-auto -mt-8 mb-4 w-full", "max-w-[80%] sm:max-w-[80%] md:max-w-[80%] lg:max-w-full"]);
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      align: "center",
      loop: true,
      skipSnaps: true
    };
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== current || $[32] !== focusedIndex || $[33] !== handleCardClick || $[34] !== handleVideoEnd || $[35] !== isAutoPlay || $[36] !== isDragging || $[37] !== isMobile || $[38] !== isMuted || $[39] !== isPaused || $[40] !== toggleMute || $[41] !== videoData) {
    let t19;
    if ($[43] !== current || $[44] !== focusedIndex || $[45] !== handleCardClick || $[46] !== handleVideoEnd || $[47] !== isAutoPlay || $[48] !== isDragging || $[49] !== isMobile || $[50] !== isMuted || $[51] !== isPaused || $[52] !== toggleMute) {
      t19 = (video, index_0) => <CarouselItem key={video.videoId} className={cn(["overflow-visible py-8", "basis-3/4 pl-4 sm:basis-1/2 md:basis-1/2 md:pl-6 lg:basis-1/3"])} onClick={() => handleCardClick(index_0)}><VideoCard videoUrl={isMobile ? video.url360 : video.url480} isSelected={current === index_0} isFocused={focusedIndex === index_0} isDragging={isDragging} isAutoPlay={isAutoPlay} isPaused={isPaused} setIsPaused={setIsPaused} isMuted={isMuted} toggleMute={toggleMute} onUserInteraction={handleUserInteraction} onVideoEnd={handleVideoEnd} className="aspect-9/16" /></CarouselItem>;
      $[43] = current;
      $[44] = focusedIndex;
      $[45] = handleCardClick;
      $[46] = handleVideoEnd;
      $[47] = isAutoPlay;
      $[48] = isDragging;
      $[49] = isMobile;
      $[50] = isMuted;
      $[51] = isPaused;
      $[52] = toggleMute;
      $[53] = t19;
    } else {
      t19 = $[53];
    }
    t18 = videoData.map(t19);
    $[31] = current;
    $[32] = focusedIndex;
    $[33] = handleCardClick;
    $[34] = handleVideoEnd;
    $[35] = isAutoPlay;
    $[36] = isDragging;
    $[37] = isMobile;
    $[38] = isMuted;
    $[39] = isPaused;
    $[40] = toggleMute;
    $[41] = videoData;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[54] !== t18) {
    t19 = <CarouselContent className="overflow-visible! -ml-4 md:-ml-6">{t18}</CarouselContent>;
    $[54] = t18;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  const t20 = !canScrollPrev;
  let t21;
  if ($[56] !== handlePrev || $[57] !== t20) {
    t21 = <CarouselPrevious variant="link" className="-left-12 md:-left-16" onClick={handlePrev} disabled={t20} />;
    $[56] = handlePrev;
    $[57] = t20;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  const t22 = !canScrollNext;
  let t23;
  if ($[59] !== handleNext || $[60] !== t22) {
    t23 = <CarouselNext variant="link" className="-right-12 md:-right-16" onClick={handleNext} disabled={t22} />;
    $[59] = handleNext;
    $[60] = t22;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  let t24;
  if ($[62] !== t19 || $[63] !== t21 || $[64] !== t23) {
    t24 = <div ref={containerRef} className={t16}><Carousel setApi={setApi} opts={t17} className="w-full overflow-visible">{t19}{t21}{t23}</Carousel></div>;
    $[62] = t19;
    $[63] = t21;
    $[64] = t23;
    $[65] = t24;
  } else {
    t24 = $[65];
  }
  return t24;
}
function _temp2() {
  const refreshTimer = setTimeout(_temp, 100);
  return () => clearTimeout(refreshTimer);
}
function _temp() {
  ScrollTrigger.refresh();
}